import React, {useState, memo, useContext} from "react";
import {useTranslation} from "react-i18next";
//local
import LoadDocsColumn from "components/LoadDocsColumn";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {getFileUID} from "../api/contracts";
import {IconDocumentPen, IconPlus, IconRules, IconDocumentList} from "components/icons";
import {docLabel} from "constants/file.consts";
import {removeFilesConfirmModal, hasPermission} from "helpers/helper";
import {SignContractContext} from "../SignContractContext";
import {LockValue} from "components/LockValue";
import {fileExtension} from "constants/file.consts";

const placeholder = {
  contracts: "SIGN_CONTRACT_DATA_FILES_CONTRACTS_COLUMN_PLACEHOLDER",
  documents: "SIGN_CONTRACT_DATA_FILES_DOCUMENTS_COLUMN_PLACEHOLDER",
  plans: "SIGN_CONTRACT_DATA_FILES_PLANS_COLUMN_PLACEHOLDER",
};

const SignContractFiles = memo(
  ({contract, columnTitles, columnAddPermissions, onChange, onCreateManualFile}) => {
    const {t} = useTranslation();
    const {status, user} = useContext(SignContractContext);

    const [loading, setLoading] = useState(false);
    const [loadColumnTitle, setLoadColumnTitle] = useState("");
    const [removeFileLoading, setRemoveFileLoading] = useState(false);

    const isCreateContractButton = status.isDraft && !contract.files?.contracts?.length;
    const canCreateContractFile = hasPermission(["CREATE_SIGN_CONTRACT_FILE"]);

    const clickAdd = (filesData, title) => {
      setLoadColumnTitle(title);
      setLoading(true);
      const uploadFiles = filesData.map((fileData) => getFileUID(fileData));
      Promise.all(uploadFiles)
        .then((results) =>
          onChange(
            "files",
            {
              ...(contract.files ?? {}),
              [docLabel[title]]: [
                ...(contract.files?.[docLabel[title]] ?? []),
                ...(Array.isArray(results) ? results : [results]),
              ],
            },
            "refresh"
          )
        )
        .finally(() => setLoading(false));
    };

    const clickRemoveFile = (id, title) =>
      removeFilesConfirmModal(id, (id) => removeFile({id, title}));

    const removeFile = (values) => {
      const {id, title} = values;
      setRemoveFileLoading(true);
      onChange(
        "files",
        {
          ...(contract.files ?? {}),
          [docLabel[title]]: id
            ? (contract.files?.[docLabel[title]] ?? [])?.filter((val) => val.id !== id)
            : [],
        },
        "refresh"
      ).finally(() => setRemoveFileLoading(false));
    };

    const handleCreateContractData = (e) => {
      if (!canCreateContractFile) return;
      else e.stopPropagation();
      const values = {
        initiatingContactFullName: `${user.firstName} ${user.lastName}`,
        initiatingContactEmail: user.email,
        initiatingContactMobilePhone: user.mobilePhone,
      };
      contract.HsContractFileDatum
        ? onCreateManualFile(true)
        : onChange(undefined, {HsContractFileDatum: values}, "update").then(() =>
            onCreateManualFile(true)
          );
    };

    const CreateContractButton = () => (
      <div className="df-row-center" style={{gap: "10px"}}>
        <span className=" option fz-12">{t("HANDYSIGN_BUTTON_SEPARATOR_TEXT")}</span>
        <LockValue
          canPermissions={["CREATE_SIGN_CONTRACT_FILE"]}
          needLabel
          needShowModalToSubscribe
        >
          <ButtonTransparent
            icon={<IconPlus />}
            name={t("SIGN_CONTRACT_DATA_FILEDATA_CREATE_BUTTON")}
            type="button"
            small
            disabled={loading}
            onClick={handleCreateContractData}
          />
        </LockValue>
      </div>
    );

    return (
      <div className="df-row-start full-width">
        <LoadDocsColumn
          title={columnTitles.contracts}
          docPlaceholder={placeholder.contracts}
          titleIcon={<IconDocumentPen />}
          docs={contract.files?.contracts ?? []}
          canAddPermissions={columnAddPermissions?.contracts}
          button="ADD"
          editable={status.isDraft}
          addFile={clickAdd}
          removeFile={clickRemoveFile}
          customFileTypes={[fileExtension.PDF]}
          customFileSize={102400}
          loading={loading}
          loadColumnTitle={loadColumnTitle}
          removeLoading={removeFileLoading}
          multiple={false}
          maxDocs={1}
          wide
          highlightIfEmpty
          addClass="signing_contract__data__file-column"
          suffixButtonContent={isCreateContractButton && <CreateContractButton />}
        />
        <LoadDocsColumn
          title={columnTitles.documents}
          docPlaceholder={placeholder.documents}
          titleIcon={<IconDocumentList />}
          docs={contract.files?.documents ?? []}
          canAddPermissions={columnAddPermissions?.documents}
          button="ADD"
          editable={status.isDraft}
          addFile={clickAdd}
          removeFile={clickRemoveFile}
          customFileTypes={[fileExtension.PDF]}
          customFileSize={102400}
          loading={loading}
          loadColumnTitle={loadColumnTitle}
          removeLoading={removeFileLoading}
          canRemoveAllFiles
          wide
          addClass="signing_contract__data__file-column"
        />
        <LoadDocsColumn
          title={columnTitles.plans}
          docPlaceholder={placeholder.plans}
          titleIcon={<IconRules />}
          docs={contract.files?.plans ?? []}
          canAddPermissions={columnAddPermissions?.plans}
          button="ADD"
          editable={status.isDraft}
          addFile={clickAdd}
          removeFile={clickRemoveFile}
          customFileTypes={[fileExtension.PDF]}
          customFileSize={102400}
          loading={loading}
          loadColumnTitle={loadColumnTitle}
          removeLoading={removeFileLoading}
          canRemoveAllFiles
          wide
          addClass="signing_contract__data__file-column"
        />
      </div>
    );
  }
);

export default SignContractFiles;
